import React from 'react';
import { useTranslation } from 'react-i18next';
import { BarChart3, Users, Calendar, Check, AlertCircle } from 'lucide-react';

export default function EnrollmentPreview({ 
    selectedMembers, 
    selectedSessions, 
    onConfirm,
    sendNotification,
    onToggleNotification 
}) {
    const { t } = useTranslation();
    const totalEnrollments = selectedMembers.length * selectedSessions.length;
    const canConfirm = selectedMembers.length > 0 && selectedSessions.length > 0;

    return (
        <div className="bg-white dark:bg-gray-900 rounded-lg shadow p-6">
            <div className="flex items-center gap-2 mb-4">
                <BarChart3 className="text-green-600" size={24} />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    {t('gym.bulkEnrollment.preview.title')}
                </h3>
            </div>

            <div className="space-y-4">
                <div className="grid grid-cols-3 gap-4">
                    <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-1">
                            <Users className="text-blue-600" size={20} />
                            <span className="text-sm text-gray-600 dark:text-white/65">
                                {t('gym.bulkEnrollment.preview.users')}
                            </span>
                        </div>
                        <p className="text-2xl font-bold text-gray-900 dark:text-white">
                            {selectedMembers.length}
                        </p>
                    </div>

                    <div className="bg-indigo-50 dark:bg-indigo-900/20 rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-1">
                            <Calendar className="text-indigo-600" size={20} />
                            <span className="text-sm text-gray-600 dark:text-white/65">
                                {t('gym.bulkEnrollment.preview.sessions')}
                            </span>
                        </div>
                        <p className="text-2xl font-bold text-gray-900 dark:text-white">
                            {selectedSessions.length}
                        </p>
                    </div>

                    <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-1">
                            <Check className="text-green-600" size={20} />
                            <span className="text-sm text-gray-600 dark:text-white/65">
                                {t('gym.bulkEnrollment.preview.total')}
                            </span>
                        </div>
                        <p className="text-2xl font-bold text-gray-900 dark:text-white">
                            {totalEnrollments}
                        </p>
                    </div>
                </div>

                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 space-y-2">
                    <div className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-blue-500 rounded-full mt-1.5"></div>
                        <p className="text-sm text-gray-700 dark:text-white/85">
                            <span className="font-semibold">{selectedMembers.length}</span> {t('gym.bulkEnrollment.preview.usersWillBeEnrolled')}
                        </p>
                    </div>
                    <div className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-indigo-500 rounded-full mt-1.5"></div>
                        <p className="text-sm text-gray-700 dark:text-white/85">
                            <span className="font-semibold">{selectedSessions.length}</span> {t('gym.bulkEnrollment.preview.sessionsSelected')}
                        </p>
                    </div>
                    <div className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-green-500 rounded-full mt-1.5"></div>
                        <p className="text-sm text-gray-700 dark:text-white/85">
                            <span className="font-semibold">{totalEnrollments}</span> {t('gym.bulkEnrollment.preview.enrollmentsWillBeCreated')} 
                            ({selectedMembers.length} × {selectedSessions.length})
                        </p>
                    </div>
                </div>

                <div className="border border-gray-200 dark:border-white/10 rounded-lg p-4">
                    <label className="flex items-center gap-3 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={sendNotification}
                            onChange={(e) => onToggleNotification(e.target.checked)}
                            className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                        />
                        <div>
                            <p className="font-medium text-gray-900 dark:text-white text-sm">
                                {t('gym.bulkEnrollment.preview.sendNotifications')}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-white/65">
                                {t('gym.bulkEnrollment.preview.sendNotificationsDescription')}
                            </p>
                        </div>
                    </label>
                </div>

                {!canConfirm && (
                    <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3 flex items-start gap-2">
                        <AlertCircle className="text-yellow-600 dark:text-yellow-500 flex-shrink-0" size={20} />
                        <p className="text-sm text-yellow-800 dark:text-yellow-300">
                            {t('gym.bulkEnrollment.preview.selectAtLeastOne')}
                        </p>
                    </div>
                )}

                {canConfirm && (
                    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 flex items-start gap-2">
                        <AlertCircle className="text-blue-600 dark:text-blue-500 flex-shrink-0" size={20} />
                        <p className="text-sm text-blue-800 dark:text-blue-300">
                            {t('gym.bulkEnrollment.preview.confirmationMessage', { count: totalEnrollments })}
                        </p>
                    </div>
                )}

                <button
                    onClick={onConfirm}
                    disabled={!canConfirm}
                    className={`w-full py-3 px-6 rounded-lg font-semibold text-white transition-colors flex items-center justify-center gap-2 ${
                        canConfirm
                            ? 'bg-green-600 hover:bg-green-700 cursor-pointer'
                            : 'bg-gray-400 cursor-not-allowed'
                    }`}
                >
                    <Check size={20} />
                    🚀 {t('gym.bulkEnrollment.preview.confirmButton')} ({totalEnrollments})
                </button>
            </div>
        </div>
    );
}
